import { useLayoutEffect, useRef } from 'react';
import { getFetchEror } from '@/app/helpers';
import { useAppDispatch } from '@/app/hooks';
import {
  useAcceptFactorOfferMutation,
  useLazyGetFactorOfferQuery,
  useLoginFactorMutation,
} from '@/entities/auth/api';
import { setCredentials } from '@/entities/authSlice';
import { eimzo } from '@/entities/eimzo';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { OfferModal } from '@/shared/modal/offer-modal';
import { UILoader } from '@/shared/ui/ui-loader';
import { Box } from '@mui/system';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { BaseModalRef } from '@/app/helpers/types';

export const AuthFactorPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const { showNotification } = useNotification();

  const token = sp.get('token');
  const tin = sp.get('t');
  const pinfl = sp.get('p');
  const isUsbLog = sp.get('isUsbLog') === 'true';

  const modalRef = useRef<BaseModalRef>(null);

  const [login] = useLoginFactorMutation();
  const [acceptOffer] = useAcceptFactorOfferMutation();
  const [getOffer, { data: offer, isLoading: isOfferLoading }] =
    useLazyGetFactorOfferQuery();

  useLayoutEffect(() => {
    if (!token) {
      return navigate('/');
    }

    authHandler();
  }, []);

  const authHandler = async () => {
    if (!token) return;
    try {
      const res = await login({ token }).unwrap();
      dispatch(
        setCredentials({
          ...res,
          userKey: token,
          cabinetType: 'factor',
          phisPersonPinfl: pinfl || '',
          mfo: res.data.mfo || '',
          isUsbLog,
        })
      );
      window.history.replaceState({}, '', window.location.pathname);
      showNotification(t('successfully_entered'), 'success');
      navigate('/factor');
    } catch (err) {
      if (err?.data?.error?.code === -124) {
        await getOffer({ userKey: token as string }).unwrap();
        modalRef.current?.open();
      } else {
        navigate('/');
        showNotification(getFetchEror(err), 'error');
      }
    }
  };

  const onOfferSign = async () => {
    if (!tin || !token || !offer?.id) return;
    try {
      const signature = await eimzo.sign(tin, tin, isUsbLog);
      await acceptOffer({ signature, userKey: token, id: offer.id }).unwrap();
      authHandler();
    } catch (err) {
      if (err) {
        showNotification(getFetchEror(err), 'error');
      } else {
        showNotification(t('errors:incorrect_password'), 'error');
      }
      navigate('/');
    }
  };

  const onOffeReject = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        height: '100vh',
      }}
    >
      {isOfferLoading && <UILoader size={80} title={t('loading') + '...'} />}
      <OfferModal
        ref={modalRef}
        cb={onOfferSign}
        pdf={offer?.base64}
        rejectCb={onOffeReject}
      />
    </Box>
  );
};
